import React, { useEffect } from 'react';
import { RadioGroup, Radio, MenuItem } from '@biq/apex-ui';
import { Grid, ListItemText, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import TextTruncate from 'react-text-truncate';
import { CreateFormFeatureDialog, HelixLoader } from '../../components';
import { SINGLE_NAVIGATION_PATHS } from '../../constants/single-navigation-paths';
import { useAlert, useUserSettings } from '../../core';
import { ElevatedSystemWallet } from '../../core/contexts/user-settings';
import { ELEVATED_SYSTEM_WALLET_KEY } from '../../core/contexts/user-settings/user-settings-helper';
import { useElevateSystemWallets } from '../../hooks';
import { commonShowAlert } from '../../lib';
import { ElevateSystemWalletEmptyState } from './ElevateSystemWalletEmptyState';
import {
    ElevateSystemWallet,
    settingsConfig,
    settingsRadioButtons,
    SettingsRadioOptionTypes,
    settingsStyles,
} from './settings-helper';

export const Settings = (): React.JSX.Element => {
    const navigate = useNavigate();
    const location = useLocation();

    const { showAlert, hideAlert, showErrorDetails } = useAlert();
    const { getElevatedSystemWallet, updateUserSettings } = useUserSettings();

    const [selectedRadioButton, setSelectedRadioButton] =
        React.useState<SettingsRadioOptionTypes>('off');
    const [selectedElevatedSystemWallet, setSelectedElevatedSystemWallet] =
        React.useState<ElevatedSystemWallet | undefined>(undefined);

    const { data, isLoading } = useElevateSystemWallets();

    const systemWalletList: ElevateSystemWallet[] = data?.data ?? [];
    const displayElevatedSystemWallets: boolean = systemWalletList.length > 0;

    useEffect(() => {
        // display elevation only when the user selected a system wallet and turned on elevation.
        if (
            getElevatedSystemWallet()?.id &&
            getElevatedSystemWallet()?.elevated
        ) {
            setSelectedRadioButton('on');
            setSelectedElevatedSystemWallet(getElevatedSystemWallet());
        }
    }, [getElevatedSystemWallet]);

    const handleMenuItemClicked = (item: ElevateSystemWallet): void => {
        const selectedSystemWallet: ElevatedSystemWallet = {
            id: item.id,
            description: item.description,
            elevated: true,
        };
        setSelectedElevatedSystemWallet(selectedSystemWallet);
    };

    const handleRadioButtonChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ): void => {
        const radioButtonValue = event.currentTarget
            .value as SettingsRadioOptionTypes;

        // try to set the previous selected system wallet.
        if (radioButtonValue === 'on') {
            setSelectedElevatedSystemWallet(getElevatedSystemWallet());
        }
        setSelectedRadioButton(radioButtonValue);
    };

    const handleClose = (): void => {
        navigate(`${location?.state ?? SINGLE_NAVIGATION_PATHS.DASHBOARD}`);
    };

    const handleCloseButtonClick = (): void => {
        handleClose();
    };

    const updateElevatedSystemWallet = (
        selectedWallet: ElevatedSystemWallet
    ): void => {
        const partialUserSettings = {
            [ELEVATED_SYSTEM_WALLET_KEY]: selectedWallet,
        };
        updateUserSettings(partialUserSettings);
    };

    const handleSaveButtonClick = (): void => {
        const isSaveAllowed: boolean =
            selectedRadioButton === 'off' ||
            (selectedRadioButton === 'on' &&
                !!selectedElevatedSystemWallet?.id);

        // only proceed to save changes if the user selected 'off', or selected 'on' and also selected a valid wallet option.
        if (!isSaveAllowed) return;

        try {
            let defaultElevatedWallet: ElevatedSystemWallet = {
                id: selectedElevatedSystemWallet?.id ?? '',
                description: selectedElevatedSystemWallet?.description ?? '',
                elevated: false,
            };
            if (selectedRadioButton === 'off') {
                // save current selected system wallet with elevation turned off
                updateElevatedSystemWallet(defaultElevatedWallet);
            } else {
                // save the selected system wallet with elevation turned on
                defaultElevatedWallet = {
                    ...defaultElevatedWallet,
                    elevated: true,
                };
                updateElevatedSystemWallet(defaultElevatedWallet);
            }
        } catch (error: any) {
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                'Unable to save save settings',
                JSON.stringify(error),
                showErrorDetails
            );
            return;
        } finally {
            commonShowAlert(
                showAlert,
                hideAlert,
                'success',
                'Settings have been updated successfully.'
            );
            handleClose();
        }
    };

    const renderMenuItem = (
        menuItem: ElevateSystemWallet
    ): React.JSX.Element => {
        return (
            <MenuItem
                sx={{
                    ...settingsStyles.menuItemStyle,
                }}
                onClick={(): void => handleMenuItemClicked(menuItem)}
            >
                <Radio
                    sx={settingsStyles.radioButton}
                    value={menuItem.id}
                    checked={selectedElevatedSystemWallet?.id === menuItem.id}
                />
                <ListItemText
                    primary={
                        <TextTruncate
                            line={1}
                            truncateText='...'
                            text={menuItem.description}
                        />
                    }
                    secondary={
                        <Grid
                            sx={settingsStyles.secondaryMenuItemTextContainer}
                        >
                            <Grid
                                sx={
                                    settingsStyles.secondaryMenuItemTextContainer
                                }
                            >
                                <Typography
                                    sx={
                                        settingsStyles.menuItemSecondaryTextBold
                                    }
                                >
                                    ID:&nbsp;
                                </Typography>
                                <Typography
                                    sx={settingsStyles.menuItemSecondaryTextId}
                                >
                                    {menuItem.id}
                                </Typography>
                            </Grid>
                            <Grid sx={settingsStyles.walletTypeTextContainer}>
                                <Typography
                                    sx={
                                        settingsStyles.menuItemSecondaryTextBold
                                    }
                                >
                                    Wallet Type:&nbsp;
                                </Typography>
                                <Typography
                                    sx={settingsStyles.menuItemSecondaryText}
                                >
                                    {menuItem.type}
                                </Typography>
                            </Grid>
                        </Grid>
                    }
                    sx={{
                        ...settingsStyles.listItemStyle,
                    }}
                />
            </MenuItem>
        );
    };

    return (
        <CreateFormFeatureDialog
            buttonText={'Save'}
            displayBackButton={false}
            handleSubmit={handleSaveButtonClick}
            displaySubmitButton={displayElevatedSystemWallets}
            customStyles={settingsStyles.root}
            handleCloseButton={handleCloseButtonClick}
            handleBackButton={(): void => {}}
            listConfig={settingsConfig}
        >
            <Grid sx={settingsStyles.mainContainer}>
                <Grid sx={settingsStyles.descriptionContainer}>
                    <Typography sx={settingsStyles.titleLabel}>
                        Elevate to System Wallet
                    </Typography>
                    <Typography sx={settingsStyles.subtitleLabel}>
                        Elevate to a system wallet for the purposes of
                        reading/writing and administering data
                    </Typography>
                </Grid>
                {displayElevatedSystemWallets ? (
                    <Grid sx={settingsStyles.elevationRadioGroupContainer}>
                        <RadioGroup
                            value={selectedRadioButton}
                            onChange={handleRadioButtonChange}
                        >
                            {settingsRadioButtons.map((val) => (
                                <Radio
                                    label={val.label}
                                    value={val.value}
                                    key={val.value}
                                />
                            ))}
                        </RadioGroup>
                        {isLoading ? (
                            <HelixLoader />
                        ) : (
                            selectedRadioButton === 'on' && (
                                <Grid
                                    sx={
                                        settingsStyles.systemWalletListContainer
                                    }
                                >
                                    <Typography
                                        sx={
                                            settingsStyles.elevateSystemWalletListText
                                        }
                                    >
                                        Choose a System Wallet
                                    </Typography>
                                    <RadioGroup>
                                        {systemWalletList.map(
                                            (item: ElevateSystemWallet) =>
                                                renderMenuItem(item)
                                        )}
                                    </RadioGroup>
                                </Grid>
                            )
                        )}
                    </Grid>
                ) : (
                    <ElevateSystemWalletEmptyState />
                )}
            </Grid>
        </CreateFormFeatureDialog>
    );
};
